<script setup>
import { ref, computed, useTemplateRef } from "vue";
import { cva } from "class-variance-authority";
import { onClickOutside } from "@vueuse/core";
import { InputSize } from "../../../interfaces/input";
const props = defineProps({
  options: {
    type: Array,
    required: true
  },
  size: {
    type: String,
    default: InputSize.LARGE
  },
  isDisabled: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: ""
  }
});
const value = defineModel({ type: [Object, null], ...{
  required: true
} });
const target = useTemplateRef("target");
const isOpen = ref(false);
const searchQuery = ref("");
const filteredOptions = computed(() => {
  return props.options.filter(
    (option) => option.value.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});
const isError = computed(() => props.error !== "" && !props.isDisabled);
const inputContainerStyles = computed(
  () => cva("pl-3 pr-2.5 py-1.5 mb-1 flex items-center gap-2 rounded-lg border", {
    variants: {
      isDefault: {
        true: "border-secondary-gray-disabled cursor-pointer"
      },
      isOpen: {
        true: "border-light-blue-primary bg-primary-light cursor-pointer"
      },
      size: {
        [InputSize.SMALL]: "h-7",
        [InputSize.MEDIUM]: "h-9",
        [InputSize.LARGE]: "h-11"
      },
      isError: {
        true: "border-form-error cursor-pointer"
      }
    }
  })({
    isDefault: !isOpen.value && !isError.value,
    isOpen: isOpen.value && !isError.value,
    size: props.size,
    isError: isError.value
  })
);
const chevronDownIconStyles = computed(
  () => cva("size-6 ml-auto", {
    variants: {
      isDefault: {
        true: "text-gray-l-800"
      },
      isDisabled: {
        true: "text-gray-disabled"
      },
      isError: {
        true: "text-icon-primary-negative"
      }
    }
  })({
    isDefault: !isError.value && !props.isDisabled,
    isDisabled: props.isDisabled,
    isError: isError.value
  })
);
const toggleDropdown = () => {
  if (props.isDisabled) return;
  isOpen.value = !isOpen.value;
};
const select = (option) => {
  value.value = option;
  toggleDropdown();
};
onClickOutside(target, () => {
  isOpen.value = false;
});
</script>

<template>
    <div ref="target" class="w-full relative">
        <div
            :class="inputContainerStyles"
            data-testid="dropdown"
            @click.prevent="toggleDropdown"
        >
            <span v-if="value" data-testid="selected-value">
                {{ value.value }}
            </span>
            <IconArrowSmallDown :class="chevronDownIconStyles" class="size-5" />
        </div>
        <span
            v-if="error !== '' && !isOpen"
            class="text-icon-secondary-negative text-[13px] font-normal leading-4 pl-4 flex"
        >
            {{ error }}
        </span>
        <div v-show="isOpen" class="px-2 absolute z-10 w-full">
            <div
                class="flex flex-col border border-secondary-gray-disabled rounded-lg bg-white"
            >
                <div
                    class="flex items-center justify-between gap-2 m-2 p-2.5 rounded-lg border border-secondary-gray-disabled"
                >
                    <IconSearch
                        class="size-5"
                        :class="
  isError ? 'text-icon-secondary-negative' : 'text-text-icon-secondary-neutral'
"
                    />
                    <input
                        v-model="searchQuery"
                        class="w-full bg-transparent border-none focus:outline-none placeholder:text-gray-l-500 text-[15px] font-normal leading-[18px] text-gray-l-800"
                        :placeholder="$t('search_dropdown.placeholder')"
                    />
                    <IconExclamationCircle
                        v-if="isError"
                        isSolid
                        class="size-5 text-icon-secondary-negative"
                    />
                    <IconXCircle
                        v-else-if="searchQuery"
                        class="size-5 text-gray-comment ml-auto cursor-pointer"
                        @click="searchQuery = ''"
                    />
                </div>

                <ul class="flex flex-col w-full overflow-y-auto max-h-[330px]">
                    <li
                        v-for="(option, index) in filteredOptions"
                        :key="option.id"
                        class="w-full flex justify-between p-4 cursor-pointer hover:bg-primary-light"
                        :class="[
  option.value === value?.value && 'bg-primary-light',
  index === filteredOptions.length - 1 && 'rounded-b-lg'
]"
                        @click="select(option)"
                    >
                        <span
                            class="text-gray-l-800 font-normal text-[15px] leading-[18px] md:leading-5 md:text-[17px] truncate"
                        >
                            {{ option.value }}
                        </span>
                        <IconCheck
                            v-if="option.value === value?.value"
                            class="size-5 text-icon-secondary-branding"
                        />
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
